import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { graphql } from '@e-socna/data-layer';

import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';

import {
  buildReferencesCacheRevision,
  isCachedReferencesStale,
  readStoredReferencesRevision,
  storeReferencesRevision,
} from '../utils/references-revision.util';

const ReferencesContext_Revision_QueryDocument = graphql(`
  query ReferencesContext_Revision_Query {
    referencesRevision
  }
`);

const REVISION_CHECK_TIMEOUT_MS = 1_500;

const REFERENCES_UPDATED_MESSAGE = 'Les référentiels ont été mis à jour.';

export const useReferencesRevisionCheck = (
  referencesQueryName: string,
  referencesDocumentHash?: string,
) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const hasCachedReferences = useMemo(
    () => queryClient.getQueryData([referencesQueryName]) !== undefined,
    [queryClient, referencesQueryName],
  );
  const [isRevisionChecked, setIsRevisionChecked] =
    useState(!hasCachedReferences);
  const [isTimeoutElapsed, setIsTimeoutElapsed] = useState(false);
  const isCheckDoneRef = useRef(false);

  const { data, isError } = useGraphQLQuery(
    ReferencesContext_Revision_QueryDocument,
    undefined,
    { gcTime: 0, retry: false, staleTime: 0 },
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsTimeoutElapsed(true);
    }, REVISION_CHECK_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!data || isCheckDoneRef.current) {
      return;
    }

    // eslint-disable-next-line functional/immutable-data
    isCheckDoneRef.current = true;

    const latestRevision = buildReferencesCacheRevision(
      data.referencesRevision,
      referencesDocumentHash,
    );
    const isStale = isCachedReferencesStale({
      hasCachedReferences,
      latestRevision,
      storedRevision: readStoredReferencesRevision(),
    });

    storeReferencesRevision(latestRevision);
    setIsRevisionChecked(true);

    if (!isStale) {
      return;
    }

    const queryFilters = { queryKey: [referencesQueryName] };
    const refresh = isTimeoutElapsed
      ? queryClient.invalidateQueries(queryFilters)
      : queryClient.resetQueries(queryFilters);

    void refresh.then(() => {
      enqueueSnackbar(REFERENCES_UPDATED_MESSAGE, { variant: 'info' });
    });
  }, [
    data,
    enqueueSnackbar,
    hasCachedReferences,
    isTimeoutElapsed,
    queryClient,
    referencesDocumentHash,
    referencesQueryName,
  ]);

  return { isReadyToRender: isRevisionChecked || isTimeoutElapsed || isError };
};
